import { RightOutlined, WarningFilled } from '@ant-design/icons';
import { Alert, Modal } from 'antd';
import dayjs from 'dayjs';
import { colors, palette } from 'design';
import { CycleStats, getOvulationAndFertilityWindow } from 'hooks/useSymptomTrackingData';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Link } from 'react-router-dom';
import { routes } from 'services/RouteService';

interface CycleDotGraphProps {
  cycle: CycleStats;
  cycleStats: { average: number; standardDeviation: number; maxVariationFromAverage: number };
  clickable?: boolean;
}

const useStyles = createUseStyles({
  clickableCycleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    cursor: 'pointer',
    '& .anticon': {
      color: colors.text.disabled,
    },
  },
  periodRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 12,
    fontSize: 16,
    width: 'fit-content',
    overflowX: 'hidden',
    maxWidth: '100%',
  },
  periodLabel: {
    width: '100%',
    fontWeight: 500,
    display: 'flex',
    justifyContent: 'space-between',
  },
  dots: {
    width: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    // A light neutral, not `text.disabled` — this is a decorative "empty"
    // dot, not text, and read too dark against the others.
    background: palette.derived.neutral200,
    display: 'inline-block',
    boxSizing: 'border-box',
  },
  dotFilled: {
    background: colors.dataViz.veryBad,
  },
  dotOvulation: {
    background: palette.teal[900],
  },
  // A lighter step than ovulation's `teal[900]`, so the fertile window reads
  // as visually distinct from it instead of both looking like the same dark
  // blue.
  dotFertile: {
    background: palette.teal[300],
  },
  dotOvulationOutline: {
    border: `2px solid ${palette.teal[900]}`,
    background: 'transparent',
  },
  dotFertileOutline: {
    border: `2px solid ${palette.teal[300]}`,
    background: 'transparent',
  },
  dotTodayRing: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    border: `2px solid ${palette.derived.tealMid}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  periodInfo: {
    fontSize: 15,
    color: colors.text.disabled,
    marginLeft: 8,
    fontWeight: 500,
  },
  currentInfo: {
    height: 18,
    lineHeight: '8px',
    background: palette.derived.tealMid,
    color: colors.text.inverse,
    borderRadius: 6,
    padding: '2px 8px 0px',
    fontSize: 13,
    marginLeft: 8,
    boxSizing: 'content-box',
  },
  cycleDetailTitle: {
    marginBottom: 0,
    color: colors.text.disabled,
    fontWeight: 'normal',
  },
  cycleDetailValue: {
    margin: 0,
    marginTop: -2,
  },
});

const CycleDotGraph: React.FC<CycleDotGraphProps> = ({ cycle, cycleStats, clickable = true }) => {
  const styles = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();

  // Use shared utility for ovulation/fertility window and modal details
  const {
    ovulationIdx,
    fertileStartIdx,
    fertileEndIdx,
    ovulationDate,
    fertileStartDate,
    fertileEndDate,
    isFertilityWindowPredictionReliable,
  } = getOvulationAndFertilityWindow(cycle, cycleStats);

  // Use indices and dates directly for rendering dots and modal details
  const startDate = cycle.start;
  const periodLength = cycle.periodLength;

  // Status of today (the last dot of an in-progress cycle), mirroring the dot
  // colors below and the labels shown at the top of the tracking survey
  const todayIdx = cycle.totalLength - 1;
  let todayDotClass = styles.dot;
  let todayStatusLabel: string | null = null;
  if (!cycle.completed) {
    if (todayIdx < periodLength) {
      todayDotClass = `${styles.dot} ${styles.dotFilled}`;
      todayStatusLabel = t('symptomTracking.cycles.legend.period');
    } else if (isFertilityWindowPredictionReliable && ovulationIdx !== null && todayIdx === ovulationIdx) {
      todayDotClass = `${styles.dot} ${styles.dotOvulationOutline}`;
      todayStatusLabel = t('symptomTracking.survey.fertility.possibleOvulationDay');
    } else if (
      isFertilityWindowPredictionReliable &&
      fertileStartIdx !== null &&
      fertileEndIdx !== null &&
      todayIdx >= fertileStartIdx &&
      todayIdx <= fertileEndIdx
    ) {
      todayDotClass = `${styles.dot} ${styles.dotFertileOutline}`;
      todayStatusLabel = t('symptomTracking.survey.fertility.possibleFertilityDay');
    }
  }

  return (
    <>
      <div
        className={styles.clickableCycleContainer}
        onClick={() => clickable && setModalOpen(true)}
        style={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        <div className={styles.periodRow}>
          <div className={styles.periodLabel}>
            {cycle.start.format('LL').replace(dayjs().year().toString(), '').trim()}
            {!cycle.completed ? (
              <span className={styles.currentInfo}>
                <Trans
                  i18nKey="symptomTracking.cycles.inProgress"
                  count={cycle.totalLength}
                  components={{ sup: <sup /> }}
                />
              </span>
            ) : (
              <span className={styles.periodInfo}>
                {cycle.totalLength} {t('symptomTracking.nextPeriod.days')}
              </span>
            )}
          </div>
          <div className={styles.dots}>
            {cycle.completed
              ? Array.from({ length: cycle.totalLength }).map((_, i) => {
                  let dotClass = styles.dot;
                  if (isFertilityWindowPredictionReliable && i === ovulationIdx) {
                    dotClass = `${styles.dot} ${styles.dotOvulation}`;
                  } else if (
                    isFertilityWindowPredictionReliable &&
                    fertileStartIdx !== null &&
                    fertileEndIdx !== null &&
                    i >= fertileStartIdx &&
                    i <= fertileEndIdx
                  ) {
                    dotClass = `${styles.dot} ${styles.dotFertile}`;
                  } else if (i < periodLength) {
                    dotClass = `${styles.dot} ${styles.dotFilled}`;
                  }
                  return <span key={i} className={dotClass} />;
                })
              : Array.from({ length: cycle.totalLength }).map((_, i) => {
                  // The last dot of the current cycle is today: ring it so it
                  // isn't confused with the ovulation dot
                  if (i === todayIdx) {
                    return (
                      <span
                        key={i}
                        className={styles.dotTodayRing}
                        title={t('symptomTracking.cycleDetail.today')}
                        data-testid="cycle-dot-today"
                      >
                        <span className={todayDotClass} />
                      </span>
                    );
                  }
                  let dotClass = styles.dot;
                  // Predict ovulation/fertility window based on average
                  if (i < periodLength) {
                    dotClass = `${styles.dot} ${styles.dotFilled}`;
                  } else {
                    if (isFertilityWindowPredictionReliable && ovulationIdx !== null && i === ovulationIdx) {
                      dotClass = `${styles.dot} ${styles.dotOvulationOutline}`;
                    } else if (
                      isFertilityWindowPredictionReliable &&
                      fertileStartIdx !== null &&
                      fertileEndIdx !== null &&
                      i >= fertileStartIdx &&
                      i <= fertileEndIdx
                    ) {
                      dotClass = `${styles.dot} ${styles.dotFertileOutline}`;
                    }
                  }
                  return <span key={i} className={dotClass} />;
                })}
          </div>
        </div>
        {clickable && <RightOutlined />}
      </div>
      <Modal footer={null} open={modalOpen} onCancel={() => setModalOpen(false)}>
        <div>
          <h3 style={{ marginTop: 0 }}>{t('symptomTracking.cycles.cycleDetails')}</h3>
          <h4 className={styles.cycleDetailTitle}>{t('symptomTracking.cycleDetail.dates')}</h4>

          <p className={styles.cycleDetailValue}>
            <b>
              {!cycle.completed ? (
                <Trans
                  i18nKey="symptomTracking.cycleDetail.datesInProgress"
                  values={{ startDate: startDate.format('LL').replace(dayjs().year().toString(), '').trim() }}
                />
              ) : (
                `${startDate.format('LL')} - ${cycle.start.add(cycle.totalLength - 1, 'day').format('LL')}`
              )}
            </b>
          </p>
          <h4 className={styles.cycleDetailTitle}>{t('symptomTracking.cycleDetail.duration')}</h4>
          <p className={styles.cycleDetailValue}>
            <b>
              {!cycle.completed ? (
                <Trans i18nKey="symptomTracking.cycleDetail.durationInProgress" />
              ) : (
                <Trans i18nKey="symptomTracking.cycleDetail.durationDays" count={cycle.totalLength} />
              )}
            </b>
          </p>
          {!cycle.completed && (
            <>
              <h4 className={styles.cycleDetailTitle}>
                <span
                  className={styles.dotTodayRing}
                  style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}
                >
                  <span className={todayDotClass} />
                </span>
                {t('symptomTracking.cycleDetail.today')}
              </h4>
              <p className={styles.cycleDetailValue}>
                <b>{dayjs().format('LL')}</b>
                {todayStatusLabel && (
                  <>
                    <br />
                    {todayStatusLabel}
                  </>
                )}
              </p>
            </>
          )}
          <h4 className={styles.cycleDetailTitle}>
            <span
              className={styles.dotFilled}
              style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 4 }}
            />
            {t('symptomTracking.cycleDetail.periodDuration')}
          </h4>
          <p className={styles.cycleDetailValue}>
            <b>
              <Trans i18nKey="symptomTracking.cycleDetail.durationDays" count={periodLength} />
            </b>
          </p>
          {isFertilityWindowPredictionReliable ? (
            <>
              <h4 className={styles.cycleDetailTitle}>
                <span
                  className={styles.dotFertile}
                  style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 4 }}
                />
                {t('symptomTracking.cycleDetail.probableFertilityPeriod')}
              </h4>
              <p className={styles.cycleDetailValue}>
                <Trans
                  i18nKey="symptomTracking.cycleDetail.probableFertilityPeriodDay"
                  values={{ start: fertileStartDate.format('dddd LL'), end: fertileEndDate.format('dddd LL') }}
                  components={{ b: <b /> }}
                />
              </p>
              <h4 className={styles.cycleDetailTitle}>
                <span
                  className={styles.dotOvulation}
                  style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 4 }}
                />
                {t('symptomTracking.cycleDetail.probableOvulationDay')}
              </h4>
              <p className={styles.cycleDetailValue}>
                <b>{ovulationDate.format('LL')}</b>
              </p>
              <Alert
                showIcon
                icon={<WarningFilled style={{ color: colors.dataViz.bad }} />}
                style={{ marginTop: 24 }}
                type="warning"
                message={t('symptomTracking.cycles.fertilityWarning')}
              />
            </>
          ) : (
            <>
              <h4 className={styles.cycleDetailTitle}>
                <span
                  className={styles.dotFertile}
                  style={{ width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 4 }}
                />
                {t('symptomTracking.cycleDetail.probableFertilityPeriod')}
              </h4>
              <Alert
                showIcon
                icon={<WarningFilled style={{ color: colors.dataViz.bad }} />}
                style={{ marginTop: 4 }}
                type="warning"
                message={
                  <Trans
                    i18nKey="symptomTracking.cycleDetail.cyclesNotRegularEnough"
                    components={{ a: <Link to={routes.messagingHome.route} /> }}
                  />
                }
              />
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

// Example component for demonstration purposes

export const CycleDotGraphExample: React.FC<{ showInProgress?: boolean }> = ({ showInProgress = false }) => {
  // Simulate a 28-day cycle with a 5-day period
  const mockCycle = {
    start: dayjs().subtract(28, 'day'),
    periodLength: 5,
    totalLength: showInProgress ? 22 : 28,
    completed: !showInProgress,
  } as any; // Type assertion for brevity

  // Simulate average stats
  const mockCycleStats = {
    average: 28,
    maxVariationFromAverage: 2,
    standardDeviation: 2,
  };

  // Minimal styles for demonstration

  return <CycleDotGraph cycle={mockCycle} cycleStats={mockCycleStats} clickable={false} />;
};

export default CycleDotGraph;
